/**
 * Mirror of server/utils/vaccinationCardCatalog.js for the React app (keep IDs in sync).
 */

export const VACCINATION_CARD_ITEM_NAME = 'Vaccination completion';
export const DETAILS_SCHEMA_VERSION = 1;
export const TB_TREATMENT_REGIMEN_OPTIONS = [
  { value: '2HRZE/4HR', label: '2HRZE/4HR (RIPE standard 6-month)' },
  { value: '9H', label: 'Isoniazid 9 months (9H)' },
  { value: '4R', label: 'Rifampin 4 months (4R)' },
  { value: '3HP', label: 'Isoniazid + rifapentine weekly 3 months (3HP)' },
  { value: 'other', label: 'Other' }
];
export const TB_IGRA_RESULT_OPTIONS = ['positive', 'negative', 'indeterminate'];
export const TB_PPD_RESULT_OPTIONS = ['gt5_high_risk', 'gt10_moderate_risk', 'gt15_low_risk'];

export const SECTIONS = [
  {
    id: 'core',
    title: 'Core Childhood & Adolescent Vaccines',
    subtitle: 'Essential vaccines for all children and adolescents',
    iconColor: 'primary'
  },
  {
    id: 'earlyChildhood',
    title: 'Early Childhood Vaccines',
    subtitle: 'Not routinely given after age 5',
    iconColor: 'purple'
  },
  {
    id: 'annual',
    title: 'Annual & Situational Vaccines',
    subtitle: 'Vaccines that require regular updates or are situation-specific',
    iconColor: 'success'
  }
];

export const VACCINES = [
  {
    id: 'hepB',
    sectionId: 'core',
    name: 'Hepatitis B',
    description: 'Protection against hepatitis B virus.',
    minDoses: 3,
    maxDoses: 3
  },
  {
    id: 'mmr',
    sectionId: 'core',
    name: 'Measles, Mumps, and Rubella (MMR)',
    description: 'Combined vaccine for three diseases.',
    minDoses: 2,
    maxDoses: 2
  },
  {
    id: 'varicella',
    sectionId: 'core',
    name: 'Varicella (Chickenpox)',
    description: 'Protection against chickenpox.',
    minDoses: 2,
    maxDoses: 2
  },
  {
    id: 'ipv',
    sectionId: 'core',
    name: 'Polio (IPV)',
    description: 'Inactivated polio vaccine.',
    minDoses: 4,
    maxDoses: 4
  },
  {
    id: 'hepA',
    sectionId: 'core',
    name: 'Hepatitis A',
    description: 'Protection against hepatitis A virus.',
    minDoses: 2,
    maxDoses: 2
  },
  {
    id: 'dtapTdap',
    sectionId: 'core',
    name: 'Diphtheria, Tetanus, and Pertussis (DTaP / Tdap)',
    description: '5 doses of childhood DTaP + 1 adolescent booster of Tdap.',
    minDoses: 6,
    maxDoses: 6
  },
  {
    id: 'hpv',
    sectionId: 'core',
    name: 'Human Papillomavirus (HPV)',
    description: '2 doses if started before age 15; 3 doses if started at 15 or older.',
    minDoses: 2,
    maxDoses: 3
  },
  {
    id: 'menACWY',
    sectionId: 'core',
    name: 'Meningococcal ACWY',
    description: 'Protection against meningococcal disease.',
    minDoses: 2,
    maxDoses: 2
  },
  {
    id: 'hib',
    sectionId: 'earlyChildhood',
    name: 'Haemophilus influenzae type b (Hib)',
    description: 'Varies by brand.',
    minDoses: 3,
    maxDoses: 4
  },
  {
    id: 'pcv',
    sectionId: 'earlyChildhood',
    name: 'Pneumococcal Conjugate (PCV)',
    description: 'Protection against pneumococcal disease.',
    minDoses: 4,
    maxDoses: 4
  },
  {
    id: 'rotavirus',
    sectionId: 'earlyChildhood',
    name: 'Rotavirus',
    description: 'Only given to infants.',
    minDoses: 2,
    maxDoses: 3
  },
  {
    id: 'influenza',
    sectionId: 'annual',
    name: 'Influenza (Flu)',
    description: 'Annual flu shot.',
    minDoses: 1,
    maxDoses: 1,
    doseLabel: '1 annually'
  },
  {
    id: 'covid19',
    sectionId: 'annual',
    name: 'COVID-19',
    description: 'At least 1 dose of the most recently updated formula.',
    minDoses: 1,
    maxDoses: 99,
    doseLabel: '1+'
  },
  {
    id: 'menB',
    sectionId: 'annual',
    name: 'Meningococcal B',
    description: "Recommended for older teens/young adults based on doctor's recommendation.",
    minDoses: 2,
    maxDoses: 3
  }
];

export function buildEmptyVaccinesState() {
  const vaccines = {};
  VACCINES.forEach((v) => {
    vaccines[v.id] = { doses: [], cardEvidencePath: '', cardEvidenceOriginalFileName: '' };
  });
  return vaccines;
}

export function buildEmptyTbScreeningState() {
  return {
    diagnosedTb: '',
    treatedTb: '',
    treatmentRegimen: '',
    treatmentRegimenOther: '',
    bcgReceived: '',
    bcgReceivedDate: '',
    igraDone: '',
    igraResult: '',
    ppdDone: '',
    ppdResult: '',
    chestXrayDone: '',
    chestXrayEvidencePath: '',
    chestXrayEvidenceOriginalFileName: ''
  };
}

/** Normalize API/stored details into the same shape as edit state (all vaccine ids, doses arrays). */
export function parseVaccinationCardDetailsToState(details) {
  const empty = buildEmptyVaccinesState();
  const emptyTb = buildEmptyTbScreeningState();
  if (!details) return { ...empty, tbScreening: emptyTb };
  try {
    const o = typeof details === 'string' ? JSON.parse(details) : details;
    const next = { ...empty };
    if (o && o.vaccines && typeof o.vaccines === 'object') {
      Object.keys(next).forEach((id) => {
        const src = o.vaccines[id];
        if (src && Array.isArray(src.doses)) {
          next[id] = {
            doses: src.doses.map((d) => {
              const row = {
                date: d.date || '',
                notes: d.notes != null ? String(d.notes) : ''
              };
              if (d.promotionEvidenceId != null) row.promotionEvidenceId = Number(d.promotionEvidenceId);
              if (d.promotionEvidencePath) row.promotionEvidencePath = String(d.promotionEvidencePath);
              if (d.promotionOriginalFileName) row.promotionOriginalFileName = String(d.promotionOriginalFileName);
              if (d.promotionAdministrationLocation) {
                row.promotionAdministrationLocation = String(d.promotionAdministrationLocation);
              }
              return row;
            }),
            cardEvidencePath: src.cardEvidencePath ? String(src.cardEvidencePath) : '',
            cardEvidenceOriginalFileName: src.cardEvidenceOriginalFileName
              ? String(src.cardEvidenceOriginalFileName)
              : ''
          };
        } else if (src && typeof src === 'object') {
          next[id] = {
            doses: [],
            cardEvidencePath: src.cardEvidencePath ? String(src.cardEvidencePath) : '',
            cardEvidenceOriginalFileName: src.cardEvidenceOriginalFileName
              ? String(src.cardEvidenceOriginalFileName)
              : ''
          };
        }
      });
    }
    const rawTb = o && o.tbScreening && typeof o.tbScreening === 'object' ? o.tbScreening : {};
    next.tbScreening = {
      ...emptyTb,
      ...rawTb,
      chestXrayEvidencePath: rawTb.chestXrayEvidencePath ? String(rawTb.chestXrayEvidencePath) : '',
      chestXrayEvidenceOriginalFileName: rawTb.chestXrayEvidenceOriginalFileName
        ? String(rawTb.chestXrayEvidenceOriginalFileName)
        : ''
    };
    return next;
  } catch {
    /* ignore */
  }
  return { ...empty, tbScreening: emptyTb };
}

/** Map promotion checklist vaccine name (director UI) to structured card id — keep in sync with server/utils/vaccinationPromotionCardMerge.js */
export function mapPromotionVaccineNameToCardVaccineId(itemName) {
  const raw = String(itemName || '').trim();
  if (!raw) return null;
  const key = raw.toLowerCase();
  const aliases = {
    'covid-19': 'covid19',
    covid19: 'covid19',
    flu: 'influenza',
    'influenza (flu)': 'influenza',
    mmr: 'mmr',
    tdap: 'dtapTdap',
    'hepatitis b': 'hepB',
    'hep b': 'hepB',
    varicella: 'varicella',
    meningococcal: 'menACWY',
    hpv: 'hpv',
    hib: 'hib',
    ipv: 'ipv',
    polio: 'ipv',
    'hepatitis a': 'hepA',
    'meningococcal b': 'menB',
    rotavirus: 'rotavirus',
    pcv: 'pcv'
  };
  if (aliases[key]) return aliases[key];
  const byCatalogName = VACCINES.find((v) => v.name.toLowerCase() === key);
  if (byCatalogName) return byCatalogName.id;
  const byId = VACCINES.find((v) => v.id.toLowerCase() === key);
  return byId ? byId.id : null;
}

export function promotionRequirementRowHasMappableVaccination(requirementsRow) {
  if (!requirementsRow) return false;
  const items = Array.isArray(requirementsRow.vaccinations) ? requirementsRow.vaccinations : [];
  return items.some((it) => it && it.required && mapPromotionVaccineNameToCardVaccineId(it.name));
}
